import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EyeIcon } from '../../../../../assets/icons/eye-2-icon.svg';
import { useAppBranding } from '../../../../../config/branding';
import DefaultApiLimit from '../../../../../config/defaultLimit';
import {
  ApiRequestPendingInterface,
  RequestPendingInterface,
} from '../../../../../interface/request-pending-interface';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import { fetchRequestApprovedAccount } from '../../../../../redux/slices/RequestApprovedAccountSlice';
import {
  disableRequestPendingAccountFilterApplied,
  fetchRequestPendingAccount,
  fetchRequestPendingAccountFilters,
  updateRequestPendingAccountFilters,
  setSortData,
} from '../../../../../redux/slices/RequestPendingAccountSlice';
import { fetchRequestRejectedAccount } from '../../../../../redux/slices/RequestRejectedAccountSlice';
import ApprovalActionBar from '../../../../../shared-components/custom-approval-action-bar/ApprovalActionBar';
import CustomTable from '../../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../../shared-components/success-notification/SuccessNotification';
import { GetParametersFromCommonFilters } from '../../../../../shared-functions/CommonFilters';
import { exportCSV, exportPDF } from '../../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../../shared-functions/ToFixedValue';
import UTCtDateoDateConvert from '../../../../../shared-functions/UTCtDateoDateConvert';
import penaltyManagementApi from '../../../../../utils/api/penalty-management';
import ViewOverrides from '../ViewOverrides';

interface ModelDetailsInterface {
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
  position?: string;
}

/**
 * Request pending component
 * @returns
 */
function RequestPendingAccount() {
  const branding = useAppBranding();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const { t } = useTranslation('penaltyManagement');
  const dispatch = useAppDispatch();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [isModelOpen, setIsModelOpen] = useState<boolean>(false);
  const [modelDetails, setModelDetails] = useState<ModelDetailsInterface>({
    title: '',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: false,
    position: 'bottom-left',
  });
  const {
    requestPending,
    limit,
    page,
    totalPages,
    totalResults,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
    loading,
  } = useAppSelector((state) => state.requestPendingAccount);

  /**
   * function for closing modal
   * @param message
   */
  const handleCloseModel = (message: string) => {
    if (
      message === 'success' ||
      message === 'rejectionSuccess' ||
      message === 'approvalSuccessMessage'
    ) {
      setIsModelOpen(true); // ✅ important
      setModelDetails({
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title={t(message)}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => setIsModelOpen(false)}
          />
        ),
        rightCorner: '',
        notification: false,
        position: 'center',
      });
    } else {
      setIsModelOpen(false);
    }
  };

  /**
   * function for opening overlay
   */
  const handleOpenModel = (currentData: RequestPendingInterface) => {
    const accountDetails = requestPending.find((value) => value._id === currentData._id);

    if (accountDetails) {
      setIsModelOpen(true);
      setModelDetails({
        title: '',
        header: false,
        content: (
          <ViewOverrides
            closeModal={handleCloseModel}
            currentData={accountDetails}
            type="Pending"
            list="account"
          />
        ),
        rightCorner: '',
        notification: false,
      });
    }
  };

  /**
   * Renders custom data for the Dynamic Pricing column
   * @param {AccountListInterface} currentRow - The current row data
   * @returns {JSX.Element} The rendered dynamic pricing cell
   */
  const renderDynamicPricing = (currentRow: RequestPendingInterface) => {
    return (
      <div className="dynamic-pricing-tags">
        {currentRow.dynamicPricing.map((item) => (
          <span key={item} className="pricing-chip">
            {item
              .replace(/([A-Z])/g, ' $1') // offPeak → off Peak
              .replace(/^./, (char) => char.toUpperCase())}
          </span>
        ))}
      </div>
    );
  };
  /**
   * Renders custom data for the Ceiling Type column
   * @param {AccountListInterface} currentRow - The current row data
   * @returns {JSX.Element} The rendered ceiling type cell
   */
  const renderCeilingType = (currentRow: RequestPendingInterface) => {
    const formattedValue = currentRow.ceilingType
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (char) => char.toUpperCase());
    return <p>{formattedValue}</p>;
  };

  const column = [
    {
      label: t('accountId'),
      accessorKey: 'accountCustomId' as keyof RequestPendingInterface,
      sortAllow: true,
    },
    {
      label: t('dynamicPricing'),
      accessorKey: 'dynamicPricing' as keyof RequestPendingInterface,
      customData: renderDynamicPricing,
      columnWidth: 200,
    },
    {
      label: t('graceInterval'),
      accessorKey: 'graceInterval' as keyof RequestPendingInterface,
      sortAllow: true,
    },
    {
      label: t('maxCeilingValue'),
      accessorKey: 'ceilingValue' as keyof RequestPendingInterface,
      sortAllow: true,
      /**
       * maxCeilingValue custom function
       */ customData: (currentRow: RequestPendingInterface) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(currentRow.ceilingValue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('ceilingType'),
      accessorKey: 'ceilingType' as keyof RequestPendingInterface,
      customData: renderCeilingType,
    },
    {
      label: t('effectiveDate'),
      accessorKey: 'effectiveFrom' as keyof RequestPendingInterface,
      sortAllow: true,
      /**
       * customData
       */
      customData: (customData: RequestPendingInterface) => (
        <div>
          {UTCtDateoDateConvert(customData.effectiveFrom)} -{' '}
          {UTCtDateoDateConvert(customData.effectiveTo)}
        </div>
      ),
    },
    {
      label: t('action'),
      accessorKey: 'action' as keyof RequestPendingInterface,
      /**
       * Rendering action
       * @returns
       */
      customData: (currentData: RequestPendingInterface) => (
        <div
          role="presentation"
          className="action-button"
          onClick={() => {
            handleOpenModel(currentData);
          }}>
          <EyeIcon />
        </div>
      ),
    },
  ];

  /**
   * Function for fetching request approved data
   * @param data
   */
  const fetchData = (data: ApiRequestPendingInterface) => {
    if (!loading) {
      dispatch(fetchRequestPendingAccount(data));
    }
  };

  /**
   * Function for sorting request approved data
   * @param sortData
   */
  const handleSort = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
    });
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageAndLimitChange = (value: ApiRequestPendingInterface) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('accountId'), accessorKey: 'accountCustomId' },
    { label: t('dynamicPricing'), accessorKey: 'dynamicPricing' },
    { label: t('graceInterval'), accessorKey: 'graceInterval' },
    { label: t('maxCeilingValue'), accessorKey: 'ceilingValue' },
    { label: t('ceilingType'), accessorKey: 'ceilingType' },
    { label: t('effectiveDate'), accessorKey: 'effectiveFrom' },
  ];
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Function for exporting request pending data
   * @param type
   */
  const exportDataPDFOrCSV = (type: 'pdf' | 'csv') => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    setExportLoader(true);
    penaltyManagementApi
      .exportAccountDataByStatus({
        payload,
        requestStatus: 'pending',
      })
      .then((response) => {
        const headers = exportCurrentColumns.map((col) => col.label);

        const rows = response.data.map((row: RequestPendingInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'accountCustomId':
                return row.accountCustomId || t('na');

              case 'stationNames':
                return Array.isArray(row.stationNames)
                  ? row.stationNames.join(', ')
                  : row.stationNames || t('na');

              case 'dynamicPricing':
                return row.dynamicPricing?.length
                  ? row.dynamicPricing.map((item) => t(item.toLowerCase())).join(', ')
                  : t('na');

              case 'graceInterval':
                return row.graceInterval ? `${row.graceInterval} Min` : t('na');

              case 'ceilingValue':
                return row.ceilingValue
                  ? `${ToFixedValue(row.ceilingValue)} ${branding.text.currency}`
                  : t('na');

              case 'ceilingType':
                return row.ceilingType ? t(row.ceilingType) : t('na');

              case 'effectiveFrom':
                return row.effectiveFrom && row.effectiveTo
                  ? `${UTCtDateoDateConvert(row.effectiveFrom)} - ${UTCtDateoDateConvert(row.effectiveTo)}`
                  : t('na');

              default:
                return t('na');
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'request-pending');
          } else {
            exportPDF(headers, rows, 'request-pending');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * handle Action Reject
   */
  const handleActionReject = async (description: string, selectedItems: string[]) => {
    try {
      await penaltyManagementApi.createAccountApprovedRejectedPermission({
        requestStatus: 'rejected',
        reason: description,
        penaltyIds: selectedItems,
      });
      handleCloseModel('rejectionSuccess');
      setExportLoader(false);
      setSelectedRows([]);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
      });
      dispatch(fetchRequestApprovedAccount({ page: DefaultApiLimit.page, limit }));
      dispatch(fetchRequestRejectedAccount({ page: DefaultApiLimit.page, limit }));
    } catch (error: any) {
      setExportLoader(false);

      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * handle Action Approve
   */
  const handleActionApprove = async (selectedItems: string[]) => {
    try {
      await penaltyManagementApi.createAccountApprovedRejectedPermission({
        requestStatus: 'approved',
        penaltyIds: selectedItems,
      });

      handleCloseModel('approvalSuccessMessage');
      setExportLoader(false);
      setSelectedRows([]);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
      });
      dispatch(fetchRequestApprovedAccount({ page: DefaultApiLimit.page, limit }));
      dispatch(fetchRequestRejectedAccount({ page: DefaultApiLimit.page, limit }));
    } catch (error: any) {
      setExportLoader(false);

      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Function for refreshing request approved data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortBy,
      sortField,
    });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (isFilterUpdated || requestPending.length === 0) {
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableRequestPendingAccountFilterApplied());
    }
  }, [filters]);

  return (
    <div className="request-pending-wrap">
      <ModalBox
        title={modelDetails.title}
        header={modelDetails.header}
        rightCorner={modelDetails.rightCorner}
        content={modelDetails.content}
        closeModal={() => setIsModelOpen(false)}
        open={isModelOpen}
        position="center"
      />
      <CustomTable
        version="v2"
        serialNo
        header={column}
        data={requestPending}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        filterListLoading={filterListLoading}
        filters={filters}
        sortData={{ sortBy, sortField }}
        loading={loading}
        handleSortChange={handleSort}
        pageLimitChange={pageAndLimitChange}
        exportData={requestPending}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPDFOrCSV}
        actionButton
        filtersToDispatch={updateRequestPendingAccountFilters}
        filterListToDispatch={fetchRequestPendingAccountFilters}
        refreshRequired
        leftTopContent={<div />}
        sizeChangeButtonRequired
        hoverLabel={t('refresh')}
        ReferenceCurrentPages={handleRefresh}
        height={window.innerHeight >= 700 ? 347 : 349}
        checkbox
        onSelectedRowsChange={setSelectedRows}
        selectedRows={selectedRows}
        selectKey="_id"
        onHiddenColumnsChange={setHiddenColumns}
      />
      <ApprovalActionBar
        selectedItems={selectedRows}
        onSubmitReject={handleActionReject}
        onSubmitApprove={handleActionApprove}
      />
    </div>
  );
}
export default RequestPendingAccount;
